import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import {
  enrichProjectsWithHoldingsProjectFields,
  downloadProjectLandingPdfById,
  fetchPlatformSettings,
  fetchPortfolio,
  fetchAcquisitionHistory,
  fetchRedemptionHistory,
  unwrapRedemptionHistoryPayload,
  fetchProjectDetails,
  fetchPriceHistory,
  fetchRedemptionBalance,
  fetchTransferHistory,
  getProjectOverviewPdfSignedUrl,
  listProjects,
  purchaseShares,
  unwrapListPayload,
  unwrapTransferHistoryPayload,
} from "../services/investmentService";
import { fetchWallet } from "../services/paymentService";
import {
  buildLifecycleBuckets,
  filterInvestorCatalogProjects,
  formatProjectCodeDisplay,
  projectTitleWithCode,
  getProjectLifecycleLabel,
  isCompletedProjectStatus,
  normalizeProjectStatus,
  formatExpectedReturnPctWithPa,
  parseProjectExpectedAnnualReturnPct,
} from "../utils/projectViews";
import MyPortfolioSection from "../components/investor/MyPortfolioSection";
import IncomingTransfersAcceptCard from "../components/investor/IncomingTransfersAcceptCard";
import FundedProjectMarketCard from "../components/investor/FundedProjectMarketCard";
import { investorLifecyclePillClass } from "../utils/investorLifecyclePill";
import SharesManagementMarketTabs from "../components/investor/SharesManagementMarketTabs";
import ShareCertificatePreviewModal from "../components/investor/ShareCertificatePreviewModal";
import RedeemSharesRequestModal from "../components/investor/RedeemSharesRequestModal";
import TransferSharesRequestModal from "../components/investor/TransferSharesRequestModal";
import InvestorRedeemSharesForm from "../components/investor/InvestorRedeemSharesForm";
import InvestorTransferSharesForm from "../components/investor/InvestorTransferSharesForm";
import LockInCountdown from "../components/LockInCountdown";
import InvestorPageHeader from "../components/investor/InvestorPageHeader";
import {
  FUNDED_PROJECTS_LABEL,
  FUNDRAISING_PROJECTS_LABEL,
  SHARES_MANAGEMENT_MODULE,
} from "../utils/investorNavConfig";
import { FUNDED_SUPPLY_REFRESH_MS } from "../utils/projectShareMetrics";
import {
  availableSharesFromHolding,
  isInvestorRedeemEligible,
  isInvestorTransferEligible,
} from "../utils/investorRedeemEligibility";
import { transferableSharesFromHolding } from "../utils/investorTransferShares";
import { redemptionRequestSuccessMessage } from "../utils/redemptionRequestSuccess";
import { transferRequestSuccessMessage } from "../utils/transferRequestSuccess";
import { useAuth } from "../store/AuthContext";
import {
  issuePricePerShare,
  primaryMarketPurchasePricePerShare,
  projectBuyValueFullIssue,
  fundraisingFundProgressPct,
  projectFundsRaisedCash,
  projectNetAssetsIssuedAtNav,
  projectCurrentValueFullIssue,
  shareValueBuyPlusPnl,
  showPortfolioNavMetrics,
  holdingBuyValueTotal,
  portfolioHoldingPricePerShare,
  portfolioHoldingValueTotal,
} from "../utils/investmentPricing";


function fundraisingDeadlineUtcMs(project) {
  const raw = String(project?.fundraising_end || "");
  const parts = raw.split("-").map((x) => Number(x));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d, 23, 59, 59, 999);
}

function splitCountdown(ms) {
  if (ms <= 0) return { days: 0, hrs: 0, min: 0, sec: 0, expired: true };
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hrs = Math.floor((totalSec % 86400) / 3600);
  const min = Math.floor((totalSec % 3600) / 60);
  const sec = totalSec % 60;
  return { days, hrs, min, sec, expired: false };
}

function projectKey(id) {
  return id == null ? "" : String(id);
}

function firstFeatureBlurb(description, fallback) {
  const t = String(description || "").trim();
  if (!t) return fallback;
  const m = t.match(/^(.+?[.!?])(\s|$)/);
  const one = m ? m[1] : t.split(/[.!?]/)[0] || t;
  const s = one.trim();
  return s.length > 160 ? `${s.slice(0, 157)}…` : s || fallback;
}

export default function InvestmentsPage() {
  const { user } = useAuth();
  const location = useLocation();
  const isAdminPreview = ["ADMIN", "SUPER_ADMIN"].includes(String(user?.role || "").toUpperCase());
  const [projects, setProjects] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [platformSettings, setPlatformSettings] = useState(null);
  const [message, setMessage] = useState("");
  const [actionSuccess, setActionSuccess] = useState(null);
  const [activeView, setActiveView] = useState("latest");
  const [search, setSearch] = useState("");
  const [purchaseForm, setPurchaseForm] = useState({ project_id: "", shares: 1, risk_acknowledged: false });
  const [redeemPendingCount, setRedeemPendingCount] = useState(0);
  const [redemptionBalances, setRedemptionBalances] = useState([]);
  const [priceByProject, setPriceByProject] = useState({});
  const [priceRefreshingId, setPriceRefreshingId] = useState(null);
  const [transferModalHolding, setTransferModalHolding] = useState(null);
  const [redeemModalBalance, setRedeemModalBalance] = useState(null);
  const [buyModalProjectId, setBuyModalProjectId] = useState("");
  const buyModalProjectIdRef = useRef("");
  buyModalProjectIdRef.current = buyModalProjectId;
  const [certPreviewProject, setCertPreviewProject] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  const [aboutProjectId, setAboutProjectId] = useState("");
  const [aboutProjectPdfUrl, setAboutProjectPdfUrl] = useState("");
  const [aboutProjectPdfLoading, setAboutProjectPdfLoading] = useState(false);
  const [aboutProjectPdfError, setAboutProjectPdfError] = useState("");
  const [activeTradeCardId, setActiveTradeCardId] = useState("");
  const activeTradeCardIdRef = useRef("");
  activeTradeCardIdRef.current = activeTradeCardId;
  const [activeTradeTab, setActiveTradeTab] = useState("buy");
  /** How the inline trade panel was opened — hides Buy when user only asked for Transfer */
  const [tradePanelOpenedAs, setTradePanelOpenedAs] = useState("");
  const [wallet, setWallet] = useState(null);
  const [fundraisingClock, setFundraisingClock] = useState(0);
  const [adminPreviewNote, setAdminPreviewNote] = useState("");
  useEffect(() => {
    const id = window.setInterval(() => setFundraisingClock((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const [purchaseSubmitting, setPurchaseSubmitting] = useState(false);
  const [projectPriceLoading, setProjectPriceLoading] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyError, setHistoryError] = useState("");
  const [historyProjectName, setHistoryProjectName] = useState("");
  const [historyType, setHistoryType] = useState("purchase");
  const historyTotals = useMemo(() => {
    return historyRows.reduce(
      (acc, row) => {
        const sh = Number(row?.shares || 0);
        acc.shares += sh;
        const lineTotal =
          Number(row?.total_bdt || 0) ||
          sh * Number(row?.price_per_share || 0);
        acc.amount += lineTotal;
        return acc;
      },
      { shares: 0, amount: 0 }
    );
  }, [historyRows]);
  const selectedProjectDetails = projects.find((p) => String(p.id) === String(selectedProject));
  const aboutProject = projects.find((p) => String(p.id) === String(aboutProjectId));
  const bdt = new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", maximumFractionDigits: 0 });
  const bdtFull = new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", maximumFractionDigits: 2 });
  const compactNumFmt = new Intl.NumberFormat("en-US", {
    notation: "compact",
    maximumFractionDigits: 1,
  });
  const intFmt = new Intl.NumberFormat("en-BD", { maximumFractionDigits: 0 });
  const shareFmt = new Intl.NumberFormat("en-BD", { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  const formatInt = (n) => intFmt.format(Number(n || 0));
  const formatShare = (n) => shareFmt.format(Number(n || 0));
  const formatBdtCompact = (n) => {
    const amount = Math.max(0, Number(n || 0));
    const full = String(Math.round(amount));
    const compact = compactNumFmt.format(amount).toUpperCase();
    return `BDT ${full}(${compact})`;
  };
  const formatDayMonthYear = (raw) => {
    if (raw == null || raw === "") return "—";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(d);
  };
  const cardPhaseClass = (p) => {
    const st = String(p.status || "").toUpperCase();
    if (st === "CANCELLED") return "invx-card--cancelled";
    if (st === "REDEEMED" || st === "COMPLETED") return "invx-card--redeemed";
    if (["MATURED", "CLOSED"].includes(st)) return "invx-card--completed";
    const total = Number(p.total_shares || 0);
    const sold = Number(p.shares_sold || 0);
    if (total > 0 && sold >= total) return "invx-card--funded";
    if (st === "LOCK_IN") return "invx-card--funded";
    return "invx-card--fundraising";
  };

  /** One-line UX hint under the title when badges don’t spell it out (fundraising uses the corner badge). */
  const investmentCardStageSubtitle = (p) => {
    const phase = cardPhaseClass(p);
    if (phase === "invx-card--fundraising") return null;
    if (phase === "invx-card--cancelled") {
      return "Cancelled — this project is no longer available for investment.";
    }
    if (phase === "invx-card--redeemed") {
      return "Redeemed — all issued shares are with the company treasury.";
    }
    if (phase === "invx-card--completed") {
      return "Matured or closed — no longer in the active secondary market.";
    }
    if (phase === "invx-card--funded") {
      return "Funded — live NAV from the asset manager; no new primary buys.";
    }
    return null;
  };

  const categorized = useMemo(() => buildLifecycleBuckets(projects, platformSettings), [projects, platformSettings]);

  useEffect(() => {
    const view = new URLSearchParams(location.search).get("view");
    if (view === "ended") {
      setActiveView("latest");
    } else if (view && ["fundraising", "funded", "latest"].includes(view)) {
      setActiveView(view);
    }
  }, [location.search]);

  const activeProjects = useMemo(() => {
    if (activeView === "latest") {
      return [
        ...categorized.fundraising,
        ...categorized.funded.filter((p) => !isCompletedProjectStatus(p.status)),
        ...categorized.completed,
      ];
    }
    if (activeView === "fundraising") return categorized.fundraising;
    if (activeView === "funded") return categorized.funded.filter((p) => !isCompletedProjectStatus(p.status));
    return [
      ...categorized.fundraising,
      ...categorized.funded.filter((p) => !isCompletedProjectStatus(p.status)),
      ...categorized.completed,
    ];
  }, [activeView, categorized]);

  const deepLinkProjectId = useMemo(() => {
    const id = new URLSearchParams(location.search).get("project_id");
    return id ? String(id).trim() : "";
  }, [location.search]);

  const searchedProjects = activeProjects.filter((p) =>
    [p.name, p.category, p.description, p.project_code, p.status]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase().trim())
  );
  const visibleProjects = useMemo(() => {
    const chronological = [...searchedProjects].sort(
      (a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
    );
    if (!deepLinkProjectId) return chronological;
    const pinned = chronological.find((p) => String(p.id) === deepLinkProjectId);
    if (!pinned) return chronological;
    return [pinned, ...chronological.filter((p) => String(p.id) !== deepLinkProjectId)];
  }, [searchedProjects, deepLinkProjectId]);
  /** Primary purchases: backend allows FUNDING only (see ``purchase_shares``). */
  const canBuyStatus = (status) => {
    const s = String(status || "").toUpperCase();
    return s === "FUNDING";
  };
  const isCompletedStatus = (status) => isCompletedProjectStatus(status);
  /** Matches backend ``secondary_market_statuses`` default (LOCK_IN,MATURED). */
  const isSecondaryMarketProjectStatus = (status) => {
    const s = String(status || "").toUpperCase();
    return s === "LOCK_IN" || s === "MATURED";
  };
  const secondaryTxnStatusLabel = (status) => {
    const s = String(status || "").toUpperCase();
    if (s === "PENDING") return "Pending";
    if (s === "COMPLETED") return "Fulfilled";
    if (s === "PARTIALLY_COMPLETED") return "Partially fulfilled";
    if (s === "REJECTED") return "Rejected";
    if (s === "CANCELLED") return "Cancelled";
    return s || "—";
  };
  const portfolioByProjectId = useMemo(() => {
    const map = new Map();
    for (const h of portfolio) {
      const pid = h?.project?.id;
      if (pid != null && pid !== "") map.set(String(pid), h);
    }
    return map;
  }, [portfolio]);
  const buyFundraisingProjects = useMemo(
    () =>
      projects.filter((p) => {
        const total = Number(p?.total_shares || 0);
        const sold = Number(p?.shares_sold || 0);
        return canBuyStatus(p.status) && total > 0 && sold < total;
      }),
    [projects]
  );
  const redeemableHoldings = useMemo(
    () =>
      portfolio.filter((h) => {
        const p = h.project;
        return (
          Number(h.shares_held || 0) > 0 &&
          p &&
          isFullySoldProject(p) &&
          isSecondaryMarketProjectStatus(p.status) &&
          p.eligible_for_redemption === true
        );
      }),
    [portfolio]
  );

  // Lock-in is computed server-side (`lock_in_active`, `redemption_unlock_at`, `eligible_for_redemption`).
  function isLockInActiveProject(p) {
    if (p && typeof p.lock_in_active === "boolean") return p.lock_in_active;
    return false;
  }

  const transferableHoldings = useMemo(
    () => portfolio.filter((h) => isInvestorTransferEligible(h, h.project)),
    [portfolio]
  );

  const marketTabCounts = useMemo(
    () => ({
      fundraising: categorized.fundraising.length,
      funded: categorized.funded.filter((p) => !isCompletedProjectStatus(p.status)).length,
      transferable: transferableHoldings.length,
      redeemPending: redeemPendingCount,
    }),
    [categorized, redeemPendingCount, transferableHoldings]
  );

  const redeemableProjectIds = useMemo(
    () => new Set(redeemableHoldings.map((h) => h.project?.id).filter((id) => id != null && id !== "").map(String)),
    [redeemableHoldings]
  );
  const transferableProjectIds = useMemo(
    () => new Set(transferableHoldings.map((h) => h.project?.id).filter((id) => id != null && id !== "").map(String)),
    [transferableHoldings]
  );

  const redemptionBalanceByProject = useMemo(() => {
    const map = new Map();
    for (const b of redemptionBalances) {
      if (b?.project_id != null && b.project_id !== "") map.set(String(b.project_id), b);
    }
    return map;
  }, [redemptionBalances]);

  const loadPriceHistoryForProject = useCallback(async (projectId) => {
    const key = projectKey(projectId);
    if (!key) return false;
    try {
      const { data } = await fetchPriceHistory(projectId);
      setPriceByProject((prev) => ({ ...prev, [key]: Array.isArray(data) ? data : [] }));
      return true;
    } catch {
      return false;
    }
  }, []);

  const refreshPriceHistory = useCallback(
    async (projectId) => {
      const key = projectKey(projectId);
      if (!key) return;
      setPriceRefreshingId(key);
      try {
        await loadPriceHistoryForProject(projectId);
      } finally {
        setPriceRefreshingId(null);
      }
    },
    [loadPriceHistoryForProject]
  );

  const fundedPriceProjectIds = useMemo(
    () => (activeView === "funded" ? visibleProjects.map((p) => p.id).filter(Boolean) : []),
    [activeView, visibleProjects]
  );

  useEffect(() => {
    if (activeView !== "funded" || !fundedPriceProjectIds.length) return undefined;
    let cancelled = false;
    (async () => {
      await Promise.all(
        fundedPriceProjectIds.map(async (pid) => {
          if (!cancelled) await loadPriceHistoryForProject(pid);
        })
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [activeView, fundedPriceProjectIds, loadPriceHistoryForProject]);

  const projectCanTransfer = useCallback(
    (project) => {
      if (!project?.id) return false;
      const holding = portfolioByProjectId.get(String(project.id));
      return isInvestorTransferEligible(holding, project);
    },
    [portfolioByProjectId]
  );

  const projectCanRedeem = useCallback(
    (project) => {
      if (!project?.id) return false;
      const holding = portfolioByProjectId.get(String(project.id));
      return isInvestorRedeemEligible(holding, project);
    },
    [portfolioByProjectId]
  );

  const openTransferModal = useCallback(
    (project) => {
      if (!project?.id || !projectCanTransfer(project)) return;
      const holding = portfolioByProjectId.get(String(project.id));
      if (!holding) return;
      setTransferModalHolding({
        ...holding,
        project_id: project.id,
        project_code: project.project_code,
        project_name: project.name,
        project,
      });
    },
    [portfolioByProjectId, projectCanTransfer]
  );

  const openRedeemModal = useCallback(
    async (project) => {
      if (!project?.id || !projectCanRedeem(project)) return;
      const pid = String(project.id);
      let balance = redemptionBalanceByProject.get(pid);
      if (!balance) {
        try {
          const { data } = await fetchRedemptionBalance(project.id);
          balance = data?.project_id ? data : null;
          if (balance) {
            setRedemptionBalances((prev) => {
              const next = prev.filter((b) => String(b.project_id) !== pid);
              return [...next, balance];
            });
          }
        } catch {
          setMessage("Could not load redemption balance for this project.");
          return;
        }
      }
      if (!balance) return;
      setRedeemModalBalance({
        ...balance,
        project_code: project.project_code,
        project_name: project.name,
      });
    },
    [projectCanRedeem, redemptionBalanceByProject]
  );

  const openSharesCertificate = useCallback(
    (project) => {
      if (!project?.id) return;
      const holding = portfolioByProjectId.get(String(project.id));
      if (!holding || Number(holding.shares_held) <= 0) return;
      setCertPreviewProject(project);
    },
    [portfolioByProjectId]
  );

  useEffect(() => {
    if (!activeTradeCardId) return;

    const isCardVisible = visibleProjects.some((p) => String(p.id) === String(activeTradeCardId));
    if (!isCardVisible) {
      setActiveTradeCardId("");
      setTradePanelOpenedAs("");
      return;
    }

    const project = projects.find((p) => String(p.id) === String(activeTradeCardId));
    if (!project) {
      setActiveTradeCardId("");
      setTradePanelOpenedAs("");
      return;
    }

    const hasHolding = Number(portfolioByProjectId.get(String(activeTradeCardId))?.shares_held || 0) > 0;
    const sharesLeft = Number(project?.total_shares || 0) - Number(project?.shares_sold || 0);
    const canBuyTab = canBuyStatus(project?.status) && sharesLeft > 0;
    const isFundedCard = isFullySoldProject(project);
    const canRedeemTab =
      hasHolding && !canBuyStatus(project?.status) && isFundedCard && redeemableProjectIds.has(String(activeTradeCardId));
    const canTransferTab =
      hasHolding && !canBuyStatus(project?.status) && isFundedCard && transferableProjectIds.has(String(activeTradeCardId));

    const availableTabs = [];
    if (canBuyTab && tradePanelOpenedAs !== "transfer") availableTabs.push("buy");
    if (canRedeemTab) availableTabs.push("redeem");
    if (canTransferTab) availableTabs.push("transfer");

    if (!availableTabs.length) {
      setActiveTradeCardId("");
      setTradePanelOpenedAs("");
      return;
    }

    if (!availableTabs.includes(activeTradeTab)) {
      setActiveTradeTab(availableTabs[0]);
    }
  }, [
    activeTradeCardId,
    activeTradeTab,
    tradePanelOpenedAs,
    portfolioByProjectId,
    projects,
    redeemableProjectIds,
    transferableProjectIds,
    visibleProjects,
  ]);

  const walletKnown = wallet != null;
  const walletBalance = walletKnown ? Number(wallet?.balance ?? 0) : 0;
  const kycApproved = String(user?.kyc_status || "").toUpperCase() === "APPROVED";
  const minPurchaseShares = Math.max(0.01, Number(platformSettings?.min_share_purchase ?? 0.1));
  const ownershipCapPct = Math.max(0, Number(platformSettings?.max_share_ownership_pct ?? 20));
  const purchaseSharesValue = Math.max(0, Number(purchaseForm.shares) || 0);
  const round2 = (n) => Math.floor(n * 100) / 100;
  const selectedListingPrice = selectedProjectDetails ? issuePricePerShare(selectedProjectDetails) : 0;
  const selectedShareNavPrice = selectedProjectDetails ? shareValueBuyPlusPnl(selectedProjectDetails) : 0;
  const purchasePricePerShare = selectedProjectDetails
    ? primaryMarketPurchasePricePerShare(selectedProjectDetails)
    : 0;
  const purchaseAtNav =
    selectedProjectDetails &&
    canBuyStatus(selectedProjectDetails.status) &&
    showPortfolioNavMetrics(selectedProjectDetails);
  const purchaseLineTotal =
    selectedProjectDetails && purchaseSharesValue > 0 ? purchaseSharesValue * purchasePricePerShare : 0;
  const projectSharesAvailable = selectedProjectDetails
    ? Math.max(
        0,
        Number(
          (
            Number(selectedProjectDetails.total_shares || 0) - Number(selectedProjectDetails.shares_sold || 0)
          ).toFixed(2)
        )
      )
    : 0;
  const heldOnSelected = selectedProjectDetails
    ? Number(portfolioByProjectId.get(String(selectedProjectDetails.id))?.shares_held || 0)
    : 0;
  const projectTotalShares = selectedProjectDetails ? Number(selectedProjectDetails.total_shares || 0) : 0;
  const maxSharesByOwnershipCap =
    selectedProjectDetails && projectTotalShares > 0
      ? Math.max(0, round2((projectTotalShares * ownershipCapPct) / 100 - heldOnSelected))
      : Number.POSITIVE_INFINITY;
  const sharePrice = purchasePricePerShare;
  const maxSharesByWallet =
    selectedProjectDetails && sharePrice > 0 ? (walletKnown ? round2(walletBalance / sharePrice) : Number.POSITIVE_INFINITY) : 0;
  const maxSharesBuyable =
    selectedProjectDetails && purchaseSharesValue >= 0
      ? Math.max(0, Math.min(projectSharesAvailable, maxSharesByOwnershipCap, maxSharesByWallet))
      : 0;
  const purchaseBlockedReason = !selectedProjectDetails
    ? ""
    : !kycApproved
      ? "Approved KYC is required before buying shares. Complete identity verification, then return here."
      : !canBuyStatus(selectedProjectDetails.status)
        ? "Purchases are only allowed while the project is in FUNDRAISING (FUNDING)."
        : purchaseSharesValue < minPurchaseShares
          ? `Enter at least ${minPurchaseShares} shares.`
          : projectSharesAvailable < 0.01
            ? "No shares are available in this project."
            : purchaseSharesValue > projectSharesAvailable
              ? `Not enough shares left in this project. Available: ${formatShare(projectSharesAvailable)}`
              : purchaseSharesValue > maxSharesByOwnershipCap
                ? (() => {
                    const capTotal = round2((projectTotalShares * ownershipCapPct) / 100);
                    const pctLabel = Number(ownershipCapPct);
                    if (heldOnSelected <= 0) {
                      return `Maximum ownership is ${pctLabel}% of this project (${formatShare(capTotal)} shares). You requested ${formatShare(purchaseSharesValue)} — you can buy at most ${formatShare(maxSharesByOwnershipCap)} shares.`;
                    }
                    if (maxSharesByOwnershipCap <= 0) {
                      return `Maximum ownership is ${pctLabel}% of this project (${formatShare(capTotal)} shares). You already hold ${formatShare(heldOnSelected)} shares from this project, so you cannot buy more.`;
                    }
                    return `Maximum ownership is ${pctLabel}% of this project (${formatShare(capTotal)} shares). You already hold ${formatShare(heldOnSelected)} shares, so you can buy at most ${formatShare(maxSharesByOwnershipCap)} more (you requested ${formatShare(purchaseSharesValue)}).`;
                  })()
                : !walletKnown
                  ? "Wallet balance is unavailable. Deposit funds first, then buy."
                  : walletKnown && purchaseLineTotal > walletBalance
                    ? "Balance is insufficient to cover the cost-deposit fund to buy."
                    : "";
  const purchaseSubmitDisabled =
    !selectedProjectDetails ||
    !kycApproved ||
    purchaseSharesValue < minPurchaseShares ||
    !purchaseForm.risk_acknowledged ||
    Boolean(purchaseBlockedReason) ||
    purchaseSubmitting;
  const progressPct = (p) => {
    const total = Math.max(1, Number(p.total_shares || 0));
    return Math.min(100, Math.round((Number(p.shares_sold || 0) / total) * 100));
  };
  function isFullySoldProject(p) {
    const total = Number(p?.total_shares || 0);
    const sold = Number(p?.shares_sold || 0);
    return total > 0 && sold >= total;
  }
  /** PNL/NAV-forward UI only when funded (LOCK_IN or sold-out live project)—not primary fundraising. */
  function isFundedStageProject(p) {
    if (!p) return false;
    const st = String(p.status || "").toUpperCase();
    if (st === "LOCK_IN") return true;
    return isFullySoldProject(p) && !isCompletedStatus(p.status);
  }
  const statusClass = (project) => {
    if (isFullySoldProject(project)) return "secondary";
    const normalized = normalizeProjectStatus(project?.status);
    if (["FUNDING", "PAUSED"].includes(normalized)) return "fundraising";
    if (["REVIEW", "OPEN"].includes(normalized)) return "running";
    if (["LOCK_IN"].includes(normalized)) return "secondary";
    if (isCompletedProjectStatus(normalized)) return "completed";
    return "running";
  };
  const stageLabel = (project) => {
    const isFullySold = isFullySoldProject(project);
    if (isFullySold && !isCompletedProjectStatus(project?.status)) return "Funded";
    return getProjectLifecycleLabel(project?.status);
  };
  const canRenderBuyAction = (project) => {
    const sharesAvailable = Number(project?.total_shares || 0) - Number(project?.shares_sold || 0);
    return canBuyStatus(project?.status) && sharesAvailable > 0;
  };
  const sectionLabel =
    activeView === "latest"
      ? "Latest Projects"
      : activeView === "fundraising"
        ? FUNDRAISING_PROJECTS_LABEL
        : activeView === "funded"
          ? FUNDED_PROJECTS_LABEL
          : "Projects";

  const applySelectedProject = (projectId) => {
    setSelectedProject(projectId);
    setPurchaseForm((prev) => ({ ...prev, project_id: projectId }));
  };
  const closeBuyModal = useCallback(() => {
    setBuyModalProjectId("");
    setMessage("");
    setPurchaseSubmitting(false);
  }, []);
  const openBuyModal = useCallback(
    (projectId) => {
      const id = String(projectId || "").trim();
      if (!id) return;
      applySelectedProject(id);
      setActiveTradeCardId("");
      setTradePanelOpenedAs("");
      setActiveTradeTab("buy");
      setMessage("");
      setPurchaseForm((prev) => ({
        ...prev,
        project_id: id,
        risk_acknowledged: false,
        shares: prev.shares && Number(prev.shares) > 0 ? prev.shares : 1,
      }));
      setBuyModalProjectId(id);
    },
    []
  );
  const openTradeCard = (projectId, tab = "buy") => {
    if (tab === "buy") {
      openBuyModal(projectId);
      return;
    }
    applySelectedProject(projectId);
    setBuyModalProjectId("");
    setActiveTradeCardId(projectId);
    setActiveTradeTab(tab);
    setTradePanelOpenedAs(tab);
  };

  useEffect(() => {
    if (!buyModalProjectId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape" && !purchaseSubmitting) closeBuyModal();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [buyModalProjectId, purchaseSubmitting, closeBuyModal]);

  useEffect(() => {
    if (!buyModalProjectId || !projects.length) return;
    const project = projects.find((p) => String(p.id) === String(buyModalProjectId));
    if (!project) {
      closeBuyModal();
      return;
    }
    const available = Number(project.total_shares || 0) - Number(project.shares_sold || 0);
    if (!canBuyStatus(project.status) || available <= 0) {
      closeBuyModal();
    }
  }, [buyModalProjectId, projects, closeBuyModal]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    const projectId = params.get("project_id");
    if (!tab || !projectId || !projects.length) return;
    const project = projects.find((p) => String(p.id) === String(projectId));
    if (!project) return;
    if (tab === "transfer") {
      setActiveView("funded");
      openTransferModal(project);
    } else if (tab === "redeem") {
      setActiveView("funded");
      openRedeemModal(project);
    }
  }, [location.search, projects, openTransferModal, openRedeemModal]);

  const closeTradeCard = () => {
    setActiveTradeCardId("");
    setTradePanelOpenedAs("");
  };

  const load = async () => {
    const previewParams = new URLSearchParams(location.search);
    const previewProjectId = previewParams.get("project_id");
    const wantsAdminPreview = isAdminPreview && previewParams.get("preview") === "1";

    const [projectsRes, portfolioRes, settingsRes, walletRes, redeemPendingRes, redeemBalRes] = await Promise.all([
      listProjects(),
      fetchPortfolio().catch(() => ({ data: [] })),
      fetchPlatformSettings(),
      fetchWallet().catch(() => null),
      fetchRedemptionHistory({ status: "PENDING", page_size: 1, page: 1 }).catch(() => ({ data: null })),
      fetchRedemptionBalance().catch(() => ({ data: null })),
    ]);
    const holdingsPayload = unwrapListPayload(portfolioRes.data);
    let fetchedProjects = filterInvestorCatalogProjects(
      enrichProjectsWithHoldingsProjectFields(unwrapListPayload(projectsRes.data), holdingsPayload)
    );

    if (wantsAdminPreview && previewProjectId) {
      const alreadyListed = fetchedProjects.some((p) => String(p.id) === String(previewProjectId));
      if (!alreadyListed) {
        try {
          const { data: detail } = await fetchProjectDetails(previewProjectId);
          if (detail?.id) {
            fetchedProjects = [detail, ...fetchedProjects];
            setAdminPreviewNote(
              "Admin preview: this project is not yet visible to investors in the live catalog (draft/review or filtered)."
            );
          }
        } catch {
          setAdminPreviewNote("Admin preview: could not load that project for the investor panel.");
        }
      } else {
        setAdminPreviewNote("Admin preview: this is the investor Shares Management view (actions disabled).");
      }
    } else if (isAdminPreview) {
      setAdminPreviewNote("Admin preview: this is the investor Shares Management view (actions disabled).");
    } else {
      setAdminPreviewNote("");
    }

    setProjects(fetchedProjects);
    setPortfolio(holdingsPayload);
    setPlatformSettings(settingsRes.data);
    if (walletRes?.data) setWallet(walletRes.data);
    if (redeemPendingRes?.data) {
      const pendingPayload = unwrapRedemptionHistoryPayload(redeemPendingRes.data);
      setRedeemPendingCount(Number(pendingPayload.summary?.pending_count ?? pendingPayload.count ?? 0));
    }
    const bal = redeemBalRes?.data;
    const balanceRows = Array.isArray(bal?.balances) ? bal.balances : bal?.project_id ? [bal] : [];
    setRedemptionBalances(balanceRows);
    const preselectedProjectId = previewProjectId;
    const n01Project = fetchedProjects.find((p) => String(p.project_code || "").toUpperCase() === "N01");
    const n01ProjectId = n01Project?.id;
    const chosenProjectId =
      preselectedProjectId && fetchedProjects.some((p) => String(p.id) === String(preselectedProjectId))
        ? preselectedProjectId
        : n01ProjectId;
    // If a buy modal or inline trade panel is open, keep forms aligned with *that* project.
    // Otherwise `applySelectedProject(N01|?project_id)` overwrites `purchaseForm.project_id` while the UI
    // still shows another card's panel — wrong `project_id` on submit and confusing totals.
    const buyId = String(buyModalProjectIdRef.current || "").trim();
    const buyStillListed = buyId && fetchedProjects.some((p) => String(p.id) === buyId);
    const tradeId = String(activeTradeCardIdRef.current || "").trim();
    const tradeStillListed = tradeId && fetchedProjects.some((p) => String(p.id) === tradeId);
    if (buyStillListed) {
      applySelectedProject(buyId);
    } else if (tradeStillListed) {
      applySelectedProject(tradeId);
    } else if (chosenProjectId) {
      applySelectedProject(chosenProjectId);
    }

    if (wantsAdminPreview && chosenProjectId) {
      const previewProject = fetchedProjects.find((p) => String(p.id) === String(chosenProjectId));
      const st = normalizeProjectStatus(previewProject?.status);
      if (["FUNDING", "PAUSED"].includes(st)) setActiveView("fundraising");
      else if (st === "LOCK_IN") setActiveView("funded");
      else setActiveView("latest");
      setAboutProjectId(String(chosenProjectId));
    }
  };

  /** Close success modal; after redemption, return to the main project card (no inline trade panel). */
  const dismissActionSuccess = () => {
    setActionSuccess((prev) => {
      if (prev?.afterDismiss === "closeTradeToCard") {
        queueMicrotask(() => {
          setActiveTradeCardId("");
          setTradePanelOpenedAs("");
          setActiveTradeTab("buy");
          load().catch(() => {});
        });
      }
      return null;
    });
  };

  useEffect(() => {
    load().catch(() => setMessage("Failed to load buy options."));
  }, []);

  const deepLinkScrollDone = useRef("");
  useEffect(() => {
    deepLinkScrollDone.current = "";
  }, [deepLinkProjectId]);

  useEffect(() => {
    if (!deepLinkProjectId || !projects.length) return undefined;
    const proj = projects.find((p) => String(p.id) === deepLinkProjectId);
    if (!proj) return undefined;
    const st = normalizeProjectStatus(proj.status);
    if (["FUNDING", "PAUSED"].includes(st)) setActiveView("fundraising");
    else if (isCompletedProjectStatus(st)) setActiveView("latest");
    else if (st === "LOCK_IN") setActiveView("funded");
    else setActiveView("latest");
    applySelectedProject(deepLinkProjectId);
    if (deepLinkScrollDone.current === deepLinkProjectId) return undefined;
    deepLinkScrollDone.current = deepLinkProjectId;
    const scrollTimer = window.setTimeout(() => {
      document.getElementById(`invx-project-${deepLinkProjectId}`)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 150);
    return () => window.clearTimeout(scrollTimer);
  }, [deepLinkProjectId, projects]);

  // Best-effort "real-time" pricing refresh for Project N01.
  useEffect(() => {
    if (!selectedProject) return;
    if (String(selectedProjectDetails?.project_code || "").toUpperCase() !== "N01") return;

    let cancelled = false;

    const refresh = async () => {
      setProjectPriceLoading(true);
      try {
        const { data } = await fetchProjectDetails(selectedProject);
        if (cancelled) return;
        setProjects((prev) => prev.map((p) => (String(p.id) === String(selectedProject) ? data : p)));
      } catch (e) {
        // Ignore best-effort failures; purchase will still be secure server-side.
      } finally {
        if (!cancelled) setProjectPriceLoading(false);
      }
    };

    refresh();
    const intervalId = setInterval(refresh, 15000);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [selectedProject, selectedProjectDetails?.project_code, fetchProjectDetails]);

  const formatPurchaseApiError = (err) => {
    const d = err.response?.data;
    if (d == null) return err.message || "Purchase failed.";
    if (typeof d.detail === "string") return d.detail;
    if (Array.isArray(d.detail)) {
      return d.detail.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join("; ");
    }
    if (typeof d === "object") {
      const parts = [];
      for (const [k, v] of Object.entries(d)) {
        if (Array.isArray(v)) parts.push(`${k}: ${v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join(", ")}`);
        else if (typeof v === "string") parts.push(`${k}: ${v}`);
        else parts.push(`${k}: ${JSON.stringify(v)}`);
      }
      if (parts.length) return parts.join(" ");
    }
    return "Purchase failed.";
  };

  const onPurchase = async (e) => {
    e.preventDefault();
    if (isAdminPreview) {
      setMessage("Admin preview only — purchases are disabled. Use an investor account to buy shares.");
      return;
    }
    if (purchaseSubmitting) return;
    const projectIdForPurchase = String(
      (buyModalProjectId && String(buyModalProjectId).trim()) ||
        (activeTradeCardId && String(activeTradeCardId).trim()) ||
        purchaseForm.project_id ||
        selectedProject ||
        ""
    ).trim();
    if (!projectIdForPurchase) {
      setMessage("Could not determine which project to buy. Close the panel and open Buy again.");
      return;
    }
    try {
      const boughtProject =
        projects.find((x) => String(x.id) === projectIdForPurchase) ||
        selectedProjectDetails ||
        null;
      setPurchaseSubmitting(true);
      setMessage("Processing purchase...");
      await purchaseShares({
        ...purchaseForm,
        project_id: projectIdForPurchase,
        shares: purchaseForm.shares,
        risk_acknowledged: Boolean(purchaseForm.risk_acknowledged),
      });
      setMessage("");
      setActionSuccess({
        title: "Share Purchase Successful",
        message: (
          <>
            Congratulations on becoming a shareholder in <strong>{boughtProject?.name || "this project"}</strong> (
            <strong>{boughtProject?.project_code || "N/A"}</strong>). Wishing you steady and sustainable returns on your
            investment.
          </>
        ),
      });
      setPurchaseForm((prev) => ({ ...prev, risk_acknowledged: false }));
      setBuyModalProjectId("");
      setActiveTradeCardId("");
      setTradePanelOpenedAs("");
      setActiveTradeTab("buy");
      await load();
    } catch (err) {
      setMessage(formatPurchaseApiError(err));
      await load();
    } finally {
      setPurchaseSubmitting(false);
    }
  };

  const onRedeemSuccess = async (data, project) => {
    await load().catch(() => {});
    setMessage("");
    const ref = data?.redemption_ref || "";
    setActionSuccess({
      title: "Redemption queued",
      afterDismiss: "closeTradeToCard",
      message: (
        <>
          {ref ? (
            <>
              Reference <strong>{ref}</strong>.{" "}
            </>
          ) : null}
          Pending admin review for {project?.project_code || "this project"}. NAV and fees are fixed at approval.
        </>
      ),
    });
  };

  const onTransferSuccess = async (data, project) => {
    await load().catch(() => {});
    setMessage("");
    setActionSuccess({
      title: "Transfer offer submitted",
      message: transferRequestSuccessMessage(data, project),
    });
  };

  const openAboutProjectPdf = async (project) => {
    if (!project?.id) return;
    try {
      const { data } = await getProjectOverviewPdfSignedUrl(project.id);
      if (data?.url) {
        window.open(data.url, "_blank", "noopener,noreferrer");
      } else {
        setMessage("Project overview PDF is unavailable.");
      }
    } catch (err) {
      setMessage(err.response?.data?.detail || "Could not open project overview PDF.");
    }
  };

  useEffect(() => {
    if (!aboutProjectId) {
      setAboutProjectPdfUrl("");
      setAboutProjectPdfLoading(false);
      setAboutProjectPdfError("");
      return;
    }

    const project = projects.find((p) => p.id === aboutProjectId);
    const hasPdfRef = Boolean(String(project?.overview_pdf_url || "").trim());
    if (!project?.id || !hasPdfRef) {
      setAboutProjectPdfUrl("");
      setAboutProjectPdfLoading(false);
      setAboutProjectPdfError("");
      return;
    }

    let cancelled = false;
    setAboutProjectPdfLoading(true);
    setAboutProjectPdfError("");
    setAboutProjectPdfUrl("");

    getProjectOverviewPdfSignedUrl(project.id)
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.url) {
          setAboutProjectPdfUrl(data.url);
        } else {
          setAboutProjectPdfError("Project overview PDF is unavailable.");
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setAboutProjectPdfError(err.response?.data?.detail || "Could not load project overview PDF.");
      })
      .finally(() => {
        if (!cancelled) setAboutProjectPdfLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [aboutProjectId, projects]);

  const loadHistoryByType = (projectId, type) => {
    if (type === "redemption") return fetchRedemptionHistory({ project_id: projectId });
    if (type === "transfer") return fetchTransferHistory({ project_id: projectId, page_size: 100 });
    return fetchAcquisitionHistory(projectId);
  };
  const historyTypeLabel = (type) => {
    if (type === "redemption") return "Redemption";
    if (type === "transfer") return "Transfer";
    if (type === "purchase") return "Buy / acquisition";
    return "Purchase";
  };
  const openHistory = async (holding, type = "purchase") => {
    const projectId = holding?.project?.id;
    if (!projectId) return;
    setHistoryType(type);
    setHistoryProjectName(holding?.project?.name || holding?.project?.project_code || "Project");
    setHistoryModalOpen(true);
    setHistoryLoading(true);
    setHistoryError("");
    setHistoryRows([]);
    try {
      const { data } = await loadHistoryByType(projectId, type);
      if (type === "redemption") {
        setHistoryRows(unwrapRedemptionHistoryPayload(data).rows);
      } else if (type === "transfer") {
        setHistoryRows(unwrapTransferHistoryPayload(data).rows);
      } else {
        setHistoryRows(Array.isArray(data) ? data : data?.results || []);
      }
    } catch (err) {
      setHistoryError(err.response?.data?.detail || `Could not load ${historyTypeLabel(type).toLowerCase()} history.`);
    } finally {
      setHistoryLoading(false);
    }
  };
  const downloadHistoryPdf = () => {
    if (!historyRows.length) return;
    const label = historyTypeLabel(historyType);
    const shareholderName = user?.full_name || "-";
    const shareholderAccountNumber = user?.account_number || "-";
    const shareholderAddress =
      user?.address || user?.present_address || user?.permanent_address || user?.mailing_address || "Not provided";
    const doc = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
    const generatedAt = new Date().toLocaleString();
    doc.setFontSize(16);
    const docTitle = historyType === "purchase" ? "Buy / acquisition history" : `${label} history`;
    doc.text(docTitle, 40, 48);
    doc.setFontSize(11);
    doc.text(`Project: ${historyProjectName || "-"}`, 40, 68);
    doc.text(`Shareholder Name: ${shareholderName}`, 40, 84);
    doc.text(`Account Number: ${shareholderAccountNumber}`, 40, 100);
    const addressLines = doc.splitTextToSize(`Address: ${shareholderAddress}`, 515);
    doc.text(addressLines, 40, 116);
    const addressBottomY = 116 + (addressLines.length - 1) * 14;
    doc.text(`Generated: ${generatedAt}`, 40, addressBottomY + 20);
    doc.text(`Total shares: ${formatShare(historyTotals.shares)}`, 40, addressBottomY + 36);
    doc.text(`Total amount: ${bdtFull.format(historyTotals.amount)}`, 40, addressBottomY + 52);
    const isAcquisitionReport = historyType === "purchase";
    const tableHead = isAcquisitionReport
      ? [["No.", "Date", "How acquired", "Shares", "Price / share"]]
      : historyType === "redemption"
        ? [["No.", "Ref", "Date", "Status", "No. of Share", "Price"]]
        : [["No.", "Date", "No. of Share", "Price"]];
    const tableBody = historyRows.map((row, idx) => {
      const dateStr = row.requested_at ? new Date(row.requested_at).toLocaleString() : "—";
      if (isAcquisitionReport) {
        return [
          String(idx + 1),
          dateStr,
          String(row.acquisition_label || "—"),
          formatShare(row.shares),
          bdtFull.format(Number(row.price_per_share || 0)),
        ];
      }
      if (historyType === "redemption") {
        return [
          String(idx + 1),
          String(row.redemption_ref || "—"),
          dateStr,
          secondaryTxnStatusLabel(row.status),
          formatShare(row.shares),
          bdtFull.format(Number(row.price_per_share || 0)),
        ];
      }
      return [String(idx + 1), dateStr, formatShare(row.shares), bdtFull.format(Number(row.price_per_share || 0))];
    });
    autoTable(doc, {
      startY: addressBottomY + 72,
      margin: { left: 40, right: 40 },
      tableWidth: "auto",
      theme: "grid",
      head: tableHead,
      body: tableBody,
      styles: {
        fontSize: 10,
        cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
        textColor: [17, 24, 39],
        lineColor: [214, 220, 229],
        lineWidth: 0.5,
        valign: "middle",
      },
      headStyles: {
        fillColor: [31, 66, 107],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      columnStyles: isAcquisitionReport
        ? {
            0: { cellWidth: 36, halign: "center" },
            1: { cellWidth: 130, halign: "left" },
            2: { cellWidth: 130, halign: "left" },
            3: { cellWidth: 72, halign: "center", fontStyle: "bold" },
            4: { cellWidth: 100, halign: "right" },
          }
        : historyType === "redemption"
          ? {
              0: { cellWidth: 28, halign: "center" },
              1: { cellWidth: 52, halign: "center" },
              2: { cellWidth: 118, halign: "left" },
              3: { cellWidth: 56, halign: "center" },
              4: { cellWidth: 56, halign: "center" },
              5: { cellWidth: 72, halign: "right" },
            }
          : {
              0: { cellWidth: 42, halign: "center" },
              1: { cellWidth: 220, halign: "left" },
              2: { cellWidth: 110, halign: "center", fontStyle: "bold" },
              3: { cellWidth: 120, halign: "right" },
            },
    });
    const safeProject = String(historyProjectName || "project").replace(/[^\w-]+/g, "_");
    const fileSlug = isAcquisitionReport ? "acquisition" : label.toLowerCase().replace(/\s+/g, "_");
    doc.save(`${fileSlug}_history_${safeProject}.pdf`);
  };

  return (
    <div className="page investor-premium investor-panel">
      {actionSuccess ? (
        <div className="invx-about-modal-overlay" onClick={dismissActionSuccess}>
          <div className="invx-about-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="invx-about-modal-close" onClick={dismissActionSuccess}>
              ×
            </button>
            <h3 style={{ marginBottom: 10 }}>{actionSuccess.title}</h3>
            <div className="msg msg-success" style={{ margin: 0, lineHeight: 1.5 }}>
              {actionSuccess.message}
            </div>
            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end" }}>
              <button type="button" className="btn-secondary compact-btn" onClick={dismissActionSuccess}>
                OK
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <InvestorPageHeader
        icon="shares-management"
        title={SHARES_MANAGEMENT_MODULE.label}
        subtitle="Discover curated projects and manage buy/sell actions."
        actions={
          <span className="invx-stat-pill">Wallet Balance {walletKnown ? bdt.format(walletBalance) : "—"}</span>
        }
      />
      {adminPreviewNote ? (
        <p className="msg" role="status" style={{ marginBottom: "0.75rem" }}>
          {adminPreviewNote}
        </p>
      ) : null}
      <p className="invx-flow-hint muted card" style={{ padding: "0.75rem 1rem", marginBottom: "1rem" }}>
        <strong>Fundraising Projects</strong> — primary offers. <strong>Funded Projects</strong> — live NAV.{" "}
        <strong>Transferable</strong> and <strong>Redeemable Projects</strong> tabs list holdings you can move or request redemption on.
      </p>
      {message && <p className="msg">{message}</p>}
      <IncomingTransfersAcceptCard
        onAccepted={() => {
          load().catch(() => {});
          setActionSuccess({
            title: "Transfer settled",
            message: <>Secondary transfer completed at locked NAV. Your wallet and holdings are updated.</>,
          });
        }}
        onError={(msg) => setMessage(msg)}
      />
      <div className="invx-filters card">
        <SharesManagementMarketTabs
          activeView={activeView}
          onViewChange={setActiveView}
          counts={marketTabCounts}
        />
        <div className="invx-filters-right">
          <input
            className="compact-field invx-filters-search"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search projects"
          />
          <select
            className="compact-field invx-filters-view"
            value={activeView}
            onChange={(e) => setActiveView(e.target.value)}
            aria-label="Catalog view"
          >
            <option value="latest">Latest</option>
            <option value="fundraising">{FUNDRAISING_PROJECTS_LABEL}</option>
            <option value="funded">{FUNDED_PROJECTS_LABEL}</option>
          </select>
        </div>
      </div>

      <div className="section-label">{sectionLabel}</div>
      {activeView === "funded" && (
        <p className="muted" style={{ margin: "0 0 10px" }}>
          After full funding: transfers anytime; redemptions after lock-in ends. Paused projects block both. Redemption requests are capped per platform rules (default 1% of your holding per request); admin settles pending requests into fulfilled or rejected.
        </p>
      )}
      <div
        className={`invx-cards-grid invx-list-layout${activeView === "funded" ? " invx-cards-grid--funded-market" : ""}`}
      >
        {visibleProjects.map((p, idx) => {
          const stageSubtitle = investmentCardStageSubtitle(p);
          const estimatedReturnWithPa = formatExpectedReturnPctWithPa(p);
          const fundraisingHeroUi = cardPhaseClass(p) === "invx-card--fundraising";
          const pct = fundraisingHeroUi ? fundraisingFundProgressPct(p) : progressPct(p);
          const deadlineMs = fundraisingDeadlineUtcMs(p);
          const countdown =
            deadlineMs != null ? splitCountdown(deadlineMs - Date.now()) : null;
          const estimatedReturnPctN = parseProjectExpectedAnnualReturnPct(p);
          const retFigure =
            estimatedReturnPctN != null
              ? `${new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 4,
                  useGrouping: false,
                }).format(estimatedReturnPctN)}%`
              : "—";
          const retHasPa = estimatedReturnPctN != null;
          const availableShareN = Math.max(
            0,
            Number((Number(p.total_shares || 0) - Number(p.shares_sold || 0)).toFixed(2))
          );
          const raisedAmt = projectFundsRaisedCash(p);
          const targetAmt = projectBuyValueFullIssue(p);
          const groundFloorCopy =
            availableShareN >= Number(p.total_shares || 0) && Number(p.total_shares || 0) > 0
              ? "All shares still available — be a founding investor."
              : availableShareN > 0
                ? `${formatShare(availableShareN)} shares still available — join this round early.`
                : "Offering is fully allocated for this round.";
          const mySharesHeld = Number(portfolioByProjectId.get(String(p.id))?.shares_held || 0);
          const listingPerShare = issuePricePerShare(p);
          const navPerShare = shareValueBuyPlusPnl(p);
          const navLiveForCard = showPortfolioNavMetrics(p);
          const buyPricePerShare = primaryMarketPurchasePricePerShare(p);
          const pidKey = projectKey(p.id);
          const showFundedMarketCard = activeView === "funded" || isFundedStageProject(p);
          const fundedHolding = portfolioByProjectId.get(String(p.id));
          const fundedTransferableQty = transferableSharesFromHolding(fundedHolding || {});
          const showSecondaryActions =
            mySharesHeld > 0 && !canBuyStatus(p.status) && isFullySoldProject(p);
          const canTransferFunded = showSecondaryActions && projectCanTransfer(p);
          const canRedeemFunded = showSecondaryActions && projectCanRedeem(p);
          const transferDisabledReason = !mySharesHeld
            ? "You need shares in this project to transfer."
            : !isFullySoldProject(p)
              ? "Transfers are available after the project is fully funded."
              : !isInvestorTransferEligible(fundedHolding, p)
                ? "This project is not transferable right now."
                : fundedTransferableQty <= 0
                  ? "No transferable shares right now (check pending redemptions or platform limits)."
                  : "";
          const redeemDisabledReason = !mySharesHeld
            ? "You need shares in this project to redeem."
            : !isFullySoldProject(p)
              ? "Redemption opens after the project is fully funded."
              : isLockInActiveProject(p)
                ? "Redemption opens after the lock-in period ends."
                : !isInvestorRedeemEligible(fundedHolding, p)
                  ? "This project is not redeemable right now."
                  : !projectCanRedeem(p)
                    ? "No shares available to redeem right now. Check lock-in rules or pending requests."
                    : "";
          const vsListingPct =
            listingPerShare > 0 ? ((navPerShare - listingPerShare) / listingPerShare) * 100 : null;
          return (
          <div
            key={p.id}
            id={`invx-project-${p.id}`}
            className={
              showFundedMarketCard
                ? `invx-funded-market-card invx-card-appear${deepLinkProjectId === String(p.id) ? " invx-card--focused" : ""}`
                : `invx-card invx-card-appear ${cardPhaseClass(p)}${fundraisingHeroUi ? " invx-card--fundraising-hero" : ""}${deepLinkProjectId === String(p.id) ? " invx-card--focused" : ""}`
            }
            style={{ animationDelay: `${idx * 0.04}s`, scrollMarginTop: 88 }}
          >
            {showFundedMarketCard ? (
              <FundedProjectMarketCard
                project={p}
                priceSeries={(pidKey && priceByProject[pidKey]) || []}
                onRefresh={pidKey ? () => refreshPriceHistory(p.id) : undefined}
                refreshing={priceRefreshingId === pidKey}
                mySharesHeld={mySharesHeld}
                canTransfer={canTransferFunded}
                canRedeem={canRedeemFunded}
                transferDisabledReason={transferDisabledReason}
                redeemDisabledReason={redeemDisabledReason}
                onTransfer={() => openTransferModal(p)}
                onRedeem={() => openRedeemModal(p)}
                onSharesCertificate={() => openSharesCertificate(p)}
              />
            ) : fundraisingHeroUi ? (
              <>
                <div className="invx-card-header invx-card-header--fr-hero">
                  <div className="invx-card-header-row">
                    <div className="invx-card-header-main">
                      <h4>{projectTitleWithCode(p)}</h4>
                    </div>
                    <div className="invx-card-badges">
                      {!isFullySoldProject(p) && !["OPEN", "REVIEW"].includes(String(p.status || "").toUpperCase()) && (
                        <span className={`invx-badge ${statusClass(p)}`}>{stageLabel(p)}</span>
                      )}
                      {isFullySoldProject(p) && !isCompletedStatus(p.status) && (
                        <span className="invx-badge secondary">Funded</span>
                      )}
                      {canBuyStatus(p.status) && Number(p.total_shares || 0) - Number(p.shares_sold || 0) > 0 && (
                        <span className="invx-badge secondary">Shares available</span>
                      )}
                      {p.category ? (
                        <span className="invx-card-category">{String(p.category).toUpperCase()}</span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="invx-fr-hero invx-fr-hero--compact" data-tick={fundraisingClock}>
                  <aside className="invx-fr-rail" aria-label="Project summary">
                    <div className="invx-fr-rail__kpis" aria-label="Headline terms">
                      <div className="invx-fr-rail__kpi">
                        <span className="invx-fr-rail__label">Est. return</span>
                        <p className="invx-fr-rail__kpi-value" data-testid="project-est-return">
                          {retFigure}
                          {retHasPa ? <span className="invx-fr-rail__kpi-pa">p.a.</span> : null}
                        </p>
                      </div>
                      <div className="invx-fr-rail__kpi">
                        <span className="invx-fr-rail__label">
                          {navLiveForCard ? "Buy price / share (NAV)" : "Price / share"}
                        </span>
                        <p className="invx-fr-rail__kpi-price">{bdtFull.format(buyPricePerShare)}</p>
                        {navLiveForCard && listingPerShare !== buyPricePerShare ? (
                          <p className="invx-fr-rail__muted" style={{ margin: "4px 0 0", fontSize: 11 }}>
                            Listing {bdtFull.format(listingPerShare)}
                          </p>
                        ) : null}
                      </div>
                    </div>
                    <div className="invx-fr-rail__countdown-block">
                      <span className="invx-fr-rail__label">Time remaining</span>
                      {countdown && !countdown.expired ? (
                        <div className="invx-fr-countdown" aria-live="polite">
                          <div className="invx-fr-countdown__unit">
                            <span className="invx-fr-countdown__num">{countdown.days}</span>
                            <span className="invx-fr-countdown__cap">Days</span>
                          </div>
                          <span className="invx-fr-countdown__sep" aria-hidden={true}>
                            :
                          </span>
                          <div className="invx-fr-countdown__unit">
                            <span className="invx-fr-countdown__num">{String(countdown.hrs).padStart(2, "0")}</span>
                            <span className="invx-fr-countdown__cap">Hrs</span>
                          </div>
                          <span className="invx-fr-countdown__sep" aria-hidden={true}>
                            :
                          </span>
                          <div className="invx-fr-countdown__unit">
                            <span className="invx-fr-countdown__num">{String(countdown.min).padStart(2, "0")}</span>
                            <span className="invx-fr-countdown__cap">Min</span>
                          </div>
                          <span className="invx-fr-countdown__sep" aria-hidden={true}>
                            :
                          </span>
                          <div className="invx-fr-countdown__unit">
                            <span className="invx-fr-countdown__num">{String(countdown.sec).padStart(2, "0")}</span>
                            <span className="invx-fr-countdown__cap">Sec</span>
                          </div>
                        </div>
                      ) : (
                        <p className="invx-fr-rail__countdown-na">{countdown?.expired ? "Closed" : "—"}</p>
                      )}
                    </div>
                  </aside>
                  <div className="invx-fr-main">
                    <section className="invx-fr-panels" aria-label="Key investment figures">
                      <div className="invx-fr-panel">
                        <h4 className="invx-fr-panel__cap">Offering</h4>
                        <dl className="invx-fr-dl">
                          <div className="invx-fr-dl__row">
                            <dt>Total shares</dt>
                            <dd>{formatInt(p.total_shares)}</dd>
                          </div>
                          <div className="invx-fr-dl__row">
                            <dt>Available</dt>
                            <dd>{formatShare(availableShareN)}</dd>
                          </div>
                          <div className="invx-fr-dl__row">
                            <dt>Shareholders</dt>
                            <dd>{formatInt(p.shareholders_count || 0)}</dd>
                          </div>
                        </dl>
                      </div>
                      <div className="invx-fr-panel">
                        <h4 className="invx-fr-panel__cap">Schedule</h4>
                        <dl className="invx-fr-dl">
                          <div className="invx-fr-dl__row invx-fr-dl__row--stack">
                            <dt>Fundraising ends</dt>
                            <dd>{p.fundraising_end ? formatDayMonthYear(p.fundraising_end) : "—"}</dd>
                          </div>
                        </dl>
                      </div>
                      <div className="invx-fr-panel">
                        <h4 className="invx-fr-panel__cap">Your position</h4>
                        <dl className="invx-fr-dl">
                          <div className="invx-fr-dl__row">
                            <dt>My shares</dt>
                            <dd>{formatShare(mySharesHeld)}</dd>
                          </div>
                          <div className="invx-fr-dl__row invx-fr-dl__row--stack">
                            <dt>Value / share</dt>
                            <dd className="invx-fr-dl__dd--wrap">
                              {navLiveForCard ? (
                                <>
                                  <span className="invx-fr-dl__strong">{bdtFull.format(navPerShare)}</span>
                                  <span className="invx-fr-dl__muted">
                                    {" "}
                                    NAV
                                    {vsListingPct != null && Number.isFinite(vsListingPct) ? (
                                      <>
                                        {" "}
                                        ({vsListingPct >= 0 ? "+" : ""}
                                        {vsListingPct.toFixed(2)}% vs listing)
                                      </>
                                    ) : null}
                                  </span>
                                </>
                              ) : (
                                <span className="invx-fr-dl__muted">
                                  Listing price is on the left. Live NAV is published after this round funds.
                                </span>
                              )}
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </section>

                    <section className="invx-fr-progress" aria-label="Fundraising progress">
                      <div className="invx-fr-progress__head">
                        <span>Progress</span>
                        <span className="invx-fr-progress__pct">{pct}% raised</span>
                      </div>
                      <div
                        className="invx-fr-progress__track"
                        role="progressbar"
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-valuenow={Math.round(Math.min(100, Math.max(0, pct)))}
                        aria-label={`Fundraising ${pct}% complete`}
                      >
                        <div className="invx-fr-progress__fill" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
                      </div>
                      <div className="invx-fr-progress__foot">
                        <span>{bdt.format(Math.round(Number(raisedAmt || 0)))} raised (cash)</span>
                        <span>Target (NAV × cap) {bdt.format(Math.round(Number(targetAmt || 0)))}</span>
                      </div>
                    </section>

                    <section className="invx-fr-highlights" aria-label="Highlights">
                      <article className="invx-fr-feature">
                        <span className="invx-fr-feature__dot invx-fr-feature__dot--mint" aria-hidden={true} />
                        <h4 className="invx-fr-feature__title">Thesis</h4>
                        <p className="invx-fr-feature__text" title={firstFeatureBlurb(p.description, "Hospital infrastructure with lasting community demand.")}>
                          {firstFeatureBlurb(p.description, "Hospital infrastructure with lasting community demand.")}
                        </p>
                      </article>
                      <article className="invx-fr-feature">
                        <span className="invx-fr-feature__dot invx-fr-feature__dot--amber" aria-hidden={true} />
                        <h4 className="invx-fr-feature__title">Return profile</h4>
                        <p className="invx-fr-feature__text">
                          The target return is shown at left. Actual outcomes depend on project performance, fees, and
                          audited reporting — not guaranteed.
                        </p>
                      </article>
                      <article className="invx-fr-feature">
                        <span className="invx-fr-feature__dot invx-fr-feature__dot--teal" aria-hidden={true} />
                        <h4 className="invx-fr-feature__title">Allocation</h4>
                        <p className="invx-fr-feature__text" title={groundFloorCopy}>
                          {groundFloorCopy}
                        </p>
                      </article>
                    </section>

                    <footer className="invx-fr-cta-row">
                      {canRenderBuyAction(p) ? (
                        <button
                          type="button"
                          className="invx-fr-cta invx-fr-cta--primary"
                          onClick={() => openTradeCard(p.id, "buy")}
                        >
                          Buy shares now <span className="invx-fr-cta__ico" aria-hidden={true}>↗</span>
                        </button>
                      ) : null}
                      <button type="button" className="invx-fr-cta invx-fr-cta--ghost" onClick={() => setAboutProjectId(p.id)}>
                        Learn more <span className="invx-fr-cta__ico" aria-hidden={true}>↗</span>
                      </button>
                    </footer>
                  </div>
                </div>
              </>
            ) : (
              <>
            <div className="invx-card-header">
              <div className="invx-card-header-row">
                <div className="invx-card-header-main">
                  <h4>{projectTitleWithCode(p)}</h4>
                  {stageSubtitle ? <p className="invx-card-stage-line">{stageSubtitle}</p> : null}
                </div>
                <div className="invx-card-badges">
                {!isFullySoldProject(p) && !["OPEN", "REVIEW"].includes(String(p.status || "").toUpperCase()) && (
                  <span className={`invx-badge ${statusClass(p)}`}>{stageLabel(p)}</span>
                )}
                {isFullySoldProject(p) && !isCompletedStatus(p.status) && (
                  <span className={`invx-badge secondary`}>Funded</span>
                )}
                {canBuyStatus(p.status) && Number(p.total_shares || 0) - Number(p.shares_sold || 0) > 0 && (
                  <span className="invx-badge secondary">Shares Available</span>
                )}
                <span className="invx-card-category">{p.category}</span>
                </div>
              </div>
            </div>
            <div className="invx-card-shell">
              <div className="invx-card-left">
                  <>
                    <div className="invx-card-block-title">Project summary</div>
                    <div className="invx-fin-grid invx-fin-grid--fundraising-flat">
                      <div className="invx-fin-grid__stack-vertical">
                        <div>
                          <small>{navLiveForCard ? "Buy price (NAV)" : "Share price"}</small>
                          <strong>{bdtFull.format(buyPricePerShare)}</strong>
                          {navLiveForCard && listingPerShare !== buyPricePerShare ? (
                            <span className="muted" style={{ display: "block", fontSize: 11, marginTop: 2 }}>
                              Listing {bdtFull.format(listingPerShare)}
                            </span>
                          ) : null}
                        </div>
                        <div data-testid="project-est-return" aria-label="Estimated annual return (p.a.)">
                          <small>Est. return</small>
                          <strong>{estimatedReturnWithPa ?? "—"}</strong>
                        </div>
                      </div>
                      <div className="invx-fin-grid__stack-vertical">
                        <div>
                          <small>Total shares</small>
                          <strong>{formatInt(p.total_shares)}</strong>
                        </div>
                        <div>
                          <small>Available shares</small>
                          <strong>{formatShare(availableShareN)}</strong>
                        </div>
                      </div>
                      <div className="invx-fin-grid__stack-vertical">
                        <div>
                          <small>Sold</small>
                          <strong>{formatShare(p.shares_sold || 0)}</strong>
                        </div>
                        <div>
                          <small>Raised (cash)</small>
                          <strong>{formatBdtCompact(projectFundsRaisedCash(p))}</strong>
                        </div>
                      </div>
                      <div className="invx-fin-grid__stack-vertical">
                        <div>
                          <small>Shareholders</small>
                          <strong>{formatInt(p.shareholders_count || 0)}</strong>
                        </div>
                        <div aria-label="Your shares in this project">
                          <small>My shares</small>
                          <strong>{formatShare(mySharesHeld)}</strong>
                        </div>
                        <div>
                          <small>Raise cap (listing)</small>
                          <strong>{formatBdtCompact(projectBuyValueFullIssue(p))}</strong>
                        </div>
                      </div>
                      <div className="invx-fin-grid__stack-two invx-fin-grid__stack-two--solo">
                        <div>
                          <small>Fundraising ends</small>
                          <strong>{formatDayMonthYear(p.fundraising_end)}</strong>
                        </div>
                      </div>
                    </div>
                  </>
              </div>
              <div className="invx-progress">
                <div className="invx-progress-head">
                  <span>Fundraising progress</span>
                  <span>{pct}%</span>
                </div>
                <div className="invx-progress-track">
                  <div className="invx-progress-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            </div>
            <div className="invx-actions invx-actions-bar invx-card-actions-row">
              <div className="invx-actions-group">
              {canRenderBuyAction(p) && (
                <button
                  type="button"
                  onClick={() => {
                    openTradeCard(p.id, "buy");
                  }}
                >
                  Buy shares
                </button>
              )}
              {portfolioByProjectId.has(String(p.id)) &&
                Number(portfolioByProjectId.get(String(p.id))?.shares_held || 0) > 0 &&
                !canBuyStatus(p.status) &&
                isFullySoldProject(p) &&
                redeemableProjectIds.has(String(p.id)) && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    openTradeCard(p.id, "redeem");
                  }}
                >
                  Redeem Shares
                </button>
              )}
              {portfolioByProjectId.has(String(p.id)) &&
                Number(portfolioByProjectId.get(String(p.id))?.shares_held || 0) > 0 &&
                !canBuyStatus(p.status) &&
                isFullySoldProject(p) &&
                transferableProjectIds.has(String(p.id)) && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    openTradeCard(p.id, "transfer");
                  }}
                >
                  Transfer Shares
                </button>
              )}
              </div>
              <button
                type="button"
                className="btn-secondary invx-about-btn"
                onClick={() => setAboutProjectId(p.id)}
              >
                About the project
              </button>
            </div>
              </>
            )}
            {!showFundedMarketCard && String(activeTradeCardId) === String(p.id) && (() => {
              const showBuyTabBtn = false; // Buy opens in a dedicated popup modal
              const showRedeemTabBtn =
                portfolioByProjectId.has(String(p.id)) &&
                Number(portfolioByProjectId.get(String(p.id))?.shares_held || 0) > 0 &&
                !canBuyStatus(p.status) &&
                isFullySoldProject(p) &&
                redeemableProjectIds.has(String(p.id));
              const showTransferTabBtn =
                portfolioByProjectId.has(String(p.id)) &&
                Number(portfolioByProjectId.get(String(p.id))?.shares_held || 0) > 0 &&
                !canBuyStatus(p.status) &&
                isFullySoldProject(p) &&
                transferableProjectIds.has(String(p.id));
              const tradeTabCount = [showBuyTabBtn, showRedeemTabBtn, showTransferTabBtn].filter(Boolean).length;
              if (!showRedeemTabBtn && !showTransferTabBtn) return null;
              return (
              <div className="invx-inline-trade">
                <button
                  type="button"
                  className="invx-inline-trade-close"
                  onClick={closeTradeCard}
                  aria-label="Close trade panel"
                >
                  ×
                </button>
                {tradeTabCount > 1 ? (
                <div className="invx-inline-trade-tabs">
                  {showRedeemTabBtn ? (
                    <button
                      type="button"
                      className={activeTradeTab === "redeem" ? "active" : ""}
                      onClick={() => setActiveTradeTab("redeem")}
                    >
                      Redeem Shares
                    </button>
                  ) : null}
                  {showTransferTabBtn ? (
                    <button
                      type="button"
                      className={activeTradeTab === "transfer" ? "active" : ""}
                      onClick={() => setActiveTradeTab("transfer")}
                    >
                      Transfer Shares
                    </button>
                  ) : null}
                </div>
                ) : null}
                {activeTradeTab === "redeem" && (
                  redeemableProjectIds.has(String(p.id)) ? (
                    <InvestorRedeemSharesForm
                      key={`redeem-${p.id}`}
                      projectId={p.id}
                      available={availableSharesFromHolding(portfolioByProjectId.get(String(p.id)))}
                      onSuccess={(data) => onRedeemSuccess(data, p)}
                      submitLabel="Submit Sell Request"
                      footerExtra={
                        <button
                          type="button"
                          className="btn-secondary compact-btn"
                          onClick={() => openHistory({ project: p }, "redemption")}
                        >
                          View Redemption History
                        </button>
                      }
                    />
                  ) : isFullySoldProject(p) && isLockInActiveProject(p) ? (
                    <div className="stack invx-trade-card invx-trade-list">
                      <h3 className="invx-trade-title">Redeem shares</h3>
                      <div className="msg">
                        <p style={{ margin: 0 }}>
                          This project is under lock-in; only share transfers are allowed until the window ends.
                        </p>
                        <LockInCountdown
                          variant="block"
                          unlockIso={p.redemption_unlock_at || p.redemptionUnlockAt}
                        />
                      </div>
                    </div>
                  ) : (
                    <div className="stack invx-trade-card invx-trade-list">
                      <h3 className="invx-trade-title">Redeem shares</h3>
                      <p className="msg">This project is not redeemable right now.</p>
                    </div>
                  )
                )}

                {activeTradeTab === "transfer" &&
                  (transferableProjectIds.has(String(p.id)) ? (
                    <InvestorTransferSharesForm
                      key={`transfer-${p.id}`}
                      projectId={p.id}
                      sharesAvailable={transferableSharesFromHolding(portfolioByProjectId.get(String(p.id)) || {})}
                      onSuccess={(data) => onTransferSuccess(data, p)}
                      submitLabel="Submit transfer"
                      footerExtra={
                        <button
                          type="button"
                          className="btn-secondary compact-btn"
                          onClick={() => openHistory({ project: p }, "transfer")}
                        >
                          View Transfer History
                        </button>
                      }
                    />
                  ) : (
                    <div className="stack invx-trade-card invx-trade-list">
                      <h3 className="invx-trade-title">Transfer shares</h3>
                      <p className="msg">This project is not transferable right now.</p>
                    </div>
                  ))}
              </div>
              );
            })()}
          </div>
        );
        })}
      </div>

      <MyPortfolioSection
        holdings={portfolio}
        redemptionBalances={redemptionBalances}
        formatShare={formatShare}
        formatMoney={(n) => bdtFull.format(n)}
        lifecyclePillClass={investorLifecyclePillClass}
      />

      {buyModalProjectId && selectedProjectDetails && String(selectedProjectDetails.id) === String(buyModalProjectId) ? (
        <div
          className="invx-about-modal-overlay buy-shares-modal-overlay"
          onClick={() => {
            if (!purchaseSubmitting) closeBuyModal();
          }}
          role="presentation"
        >
          <div
            className="invx-about-modal buy-shares-modal investor-premium"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="buy-shares-modal-title"
          >
            <button
              type="button"
              className="invx-about-modal-close"
              onClick={() => {
                if (!purchaseSubmitting) closeBuyModal();
              }}
              aria-label="Close buy shares"
              disabled={purchaseSubmitting}
            >
              ×
            </button>
            <form className="stack invx-trade-card invx-trade-list buy-shares-modal__form" onSubmit={onPurchase}>
              <h3 id="buy-shares-modal-title" className="invx-trade-title" style={{ marginTop: 0, paddingRight: 28 }}>
                Buy Share (Primary Market)
              </h3>
              <p className="invx-about-modal-project" style={{ marginTop: 0 }}>
                {projectTitleWithCode(selectedProjectDetails)}
              </p>
              <p className="muted invx-trade-sub">
                {purchaseAtNav
                  ? "Remaining unsold shares are priced at the current NAV (after approved P&L), not the original listing price."
                  : "Complete the details below to place your buy order."}
              </p>
              <div className="invx-qty-row">
                <button
                  type="button"
                  onClick={() =>
                    setPurchaseForm((prev) => ({
                      ...prev,
                      shares: round2(Math.max(minPurchaseShares, purchaseSharesValue - 0.01)),
                    }))
                  }
                  disabled={purchaseSubmitting || purchaseSharesValue <= minPurchaseShares}
                  aria-label="Decrease shares"
                >
                  -
                </button>
                <input
                  placeholder="Shares"
                  type="number"
                  step="0.01"
                  min={String(minPurchaseShares)}
                  value={purchaseForm.shares}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, shares: e.target.value })}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() =>
                    setPurchaseForm((prev) => ({
                      ...prev,
                      shares: round2(Math.min(maxSharesBuyable || minPurchaseShares, purchaseSharesValue + 0.01)),
                    }))
                  }
                  disabled={purchaseSubmitting || maxSharesBuyable <= purchaseSharesValue}
                  aria-label="Increase shares"
                >
                  +
                </button>
              </div>
              <div className="invx-quick-qty-row">
                {[1, 5, 10, 20].map((n) => {
                  const disabled = !walletKnown || n > maxSharesBuyable || n < 1;
                  return (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setPurchaseForm((prev) => ({ ...prev, shares: String(n) }))}
                      disabled={purchaseSubmitting || disabled}
                      style={{ padding: "6px 10px", fontSize: 13 }}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
              <div className="invx-purchase-summary">
                <div>
                  <small>Estimated total</small>
                  <strong>{bdt.format(purchaseLineTotal || 0)}</strong>
                </div>
                <div>
                  <small>Wallet balance</small>
                  <strong>{walletKnown ? bdt.format(walletBalance) : "—"}</strong>
                </div>
                <div>
                  <small>Est. Return</small>
                  <strong>{formatExpectedReturnPctWithPa(selectedProjectDetails) ?? "—"}</strong>
                </div>
                <div>
                  <small>{purchaseAtNav ? "Share price (NAV)" : "Share price (listing)"}</small>
                  <strong>{bdtFull.format(purchasePricePerShare)}</strong>
                  {purchaseAtNav && selectedListingPrice !== purchasePricePerShare ? (
                    <span className="muted" style={{ display: "block", fontSize: 11, marginTop: 2 }}>
                      Original listing {bdtFull.format(selectedListingPrice)}
                    </span>
                  ) : null}
                  {projectPriceLoading ? (
                    <span className="muted" style={{ marginLeft: 8 }}>
                      Updating…
                    </span>
                  ) : null}
                </div>
                <div>
                  <small>Your holdings in this project</small>
                  <strong>{formatShare(heldOnSelected)}</strong>
                </div>
                <div>
                  <small>Max you can buy now</small>
                  <strong>{walletKnown ? formatShare(maxSharesBuyable) : "—"}</strong>
                  {Number.isFinite(ownershipCapPct) ? (
                    <span className="muted" style={{ display: "block", fontSize: 11, marginTop: 2 }}>
                      {Number(ownershipCapPct)}% ownership cap
                    </span>
                  ) : null}
                </div>
              </div>
              {!kycApproved ? (
                <p className="msg msg-error">
                  Approved KYC is required before buying shares. <Link to="/kyc">Complete KYC →</Link>
                </p>
              ) : null}
              {!walletKnown ? (
                <p className="msg">
                  Wallet balance unavailable. <Link to="/wallet">Deposit funds</Link> and try again.
                </p>
              ) : null}
              {purchaseBlockedReason && kycApproved ? <p className="msg msg-error">{purchaseBlockedReason}</p> : null}
              <label className="invx-check-row" style={{ display: "flex", alignItems: "flex-start", gap: 10, lineHeight: 1.35 }}>
                <input
                  type="checkbox"
                  checked={purchaseForm.risk_acknowledged}
                  onChange={(e) => setPurchaseForm({ ...purchaseForm, risk_acknowledged: e.target.checked })}
                  style={{ width: 16, height: 16, margin: "2px 0 0", padding: 0, flex: "0 0 auto" }}
                />
                I confirm that I have read and agree to the Terms & Conditions and risk disclosure.
              </label>
              {(message || purchaseSubmitting) && (
                <p
                  className={`msg${purchaseSubmitting || message === "Processing purchase..." ? "" : " msg-error"}`}
                  style={{ marginTop: 10 }}
                  role={purchaseSubmitting ? "status" : "alert"}
                >
                  {message || "Processing purchase…"}
                </p>
              )}
              <div className="buy-shares-modal__actions">
                <button type="button" className="btn-secondary" onClick={closeBuyModal} disabled={purchaseSubmitting}>
                  Cancel
                </button>
                <button type="submit" disabled={purchaseSubmitDisabled} aria-busy={purchaseSubmitting}>
                  {purchaseSubmitting ? "Processing…" : "Buy Shares"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {transferModalHolding ? (
        <TransferSharesRequestModal
          holding={transferModalHolding}
          onClose={() => setTransferModalHolding(null)}
          onSuccess={(data) => {
            setTransferModalHolding(null);
            load().catch(() => {});
            setActionSuccess({
              title: "Transfer offer created",
              message: transferRequestSuccessMessage(data),
            });
          }}
        />
      ) : null}

      {redeemModalBalance ? (
        <RedeemSharesRequestModal
          balance={redeemModalBalance}
          onClose={() => setRedeemModalBalance(null)}
          onSuccess={(data) => {
            onRedeemSuccess(data, { id: redeemModalBalance.project_id, name: redeemModalBalance.project_name });
            setRedeemModalBalance(null);
          }}
        />
      ) : null}

      {certPreviewProject ? (
        <ShareCertificatePreviewModal project={certPreviewProject} onClose={() => setCertPreviewProject(null)} />
      ) : null}

      {aboutProject && (
        <div className="invx-about-modal-overlay" onClick={() => setAboutProjectId("")}>
          <div className="invx-about-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="invx-about-modal-close" onClick={() => setAboutProjectId("")}>×</button>
            <h3>About the project</h3>
            <p className="invx-about-modal-project">{projectTitleWithCode(aboutProject)}</p>
            <p className="invx-card-desc">{aboutProject.description || "No detailed description provided."}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
              <Link
                className="btn-secondary"
                to={
                  aboutProject.landing_slug
                    ? `/p/${aboutProject.landing_slug}`
                    : `/projects/${aboutProject.id}/about`
                }
                onClick={() => setAboutProjectId("")}
              >
                View landing page
              </Link>
              <button
                type="button"
                className="btn-secondary"
                onClick={() =>
                  downloadProjectLandingPdfById(
                    aboutProject.id,
                    aboutProject.landing_slug || aboutProject.project_code || aboutProject.name
                  ).catch(() => {})
                }
              >
                Download page PDF
              </button>
            </div>
            {!aboutProject.overview_pdf_url ? (
              <p className="muted">No project overview PDF uploaded yet.</p>
            ) : (
              <div className="invx-about-pdf-block">
                {aboutProjectPdfLoading ? <p className="muted">Loading project overview PDF...</p> : null}
                {aboutProjectPdfError ? <p className="msg msg-error">{aboutProjectPdfError}</p> : null}
                {!aboutProjectPdfLoading && !aboutProjectPdfError && aboutProjectPdfUrl ? (
                  <iframe
                    title={`Project Overview - ${aboutProject.name}`}
                    className="invx-about-pdf-frame"
                    src={aboutProjectPdfUrl}
                  />
                ) : null}
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => openAboutProjectPdf(aboutProject)}
                >
                  Open Project Overview PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {historyModalOpen && (
        <div className="invx-about-modal-overlay" onClick={() => setHistoryModalOpen(false)}>
          <div className="invx-about-modal" onClick={(e) => e.stopPropagation()}>
            <button type="button" className="invx-about-modal-close" onClick={() => setHistoryModalOpen(false)}>
              ×
            </button>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap", paddingRight: 64 }}>
              <h3 style={{ margin: 0 }}>{historyTypeLabel(historyType)} History</h3>
              <button type="button" className="btn-secondary compact-btn" style={{ whiteSpace: "nowrap", marginRight: 8 }} onClick={downloadHistoryPdf} disabled={!historyRows.length}>
                Download PDF
              </button>
            </div>
            <p className="muted" style={{ marginTop: 0 }}>{historyProjectName}</p>
            {historyLoading ? <p className="muted">Loading history...</p> : null}
            {historyError ? <p className="msg msg-error">{historyError}</p> : null}
            {!historyLoading && !historyError && historyRows.length === 0 ? (
              <p className="muted">No {historyTypeLabel(historyType).toLowerCase()} records found for this project.</p>
            ) : null}
            {!historyLoading && !historyError && historyRows.length > 0 ? (
              <>
                <div className="invx-purchase-summary" style={{ marginBottom: 10 }}>
                  <div><small>Total shares</small><strong>{formatShare(historyTotals.shares)}</strong></div>
                  <div><small>Total amount</small><strong>{bdtFull.format(historyTotals.amount)}</strong></div>
                </div>
                {historyType === "purchase" ? (
                  <p className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
                    Rows distinguish <strong>fundraising (primary issuance)</strong> from{" "}
                    <strong>funded market (secondary transfer)</strong>.
                  </p>
                ) : null}
                <div
                  className="muted"
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      historyType === "purchase"
                        ? "44px minmax(100px, 1.3fr) minmax(120px, 1.6fr) 72px minmax(88px, 1fr)"
                        : historyType === "redemption"
                          ? "40px 52px minmax(88px, 1.1fr) minmax(56px, 0.55fr) 72px minmax(72px, 1fr)"
                          : "60px 2fr 1fr 1fr",
                    gap: 10,
                    padding: "0 12px 8px",
                    fontWeight: 600,
                  }}
                >
                  <span>No.</span>
                  {historyType === "redemption" ? <span>Ref</span> : null}
                  <span>Date</span>
                  {historyType === "purchase" ? <span>How acquired</span> : null}
                  {historyType === "redemption" ? <span>Status</span> : null}
                  <span>No. of Share</span>
                  <span>{historyType === "purchase" ? "Price / share" : "Price"}</span>
                </div>
                <ul className="list">
                  {historyRows.map((row, idx) => (
                    <li key={`${row.id}-${idx}`} className="list-item">
                      <div
                        style={{
                          width: "100%",
                          display: "grid",
                          gridTemplateColumns:
                            historyType === "purchase"
                              ? "44px minmax(100px, 1.3fr) minmax(120px, 1.6fr) 72px minmax(88px, 1fr)"
                              : historyType === "redemption"
                                ? "40px 52px minmax(88px, 1.1fr) minmax(56px, 0.55fr) 72px minmax(72px, 1fr)"
                                : "60px 2fr 1fr 1fr",
                          gap: 10,
                          alignItems: "center",
                        }}
                      >
                        <span>{idx + 1}</span>
                        {historyType === "redemption" ? (
                          <span style={{ fontSize: 12 }}>{row.redemption_ref || "—"}</span>
                        ) : null}
                        <span>{row.requested_at ? new Date(row.requested_at).toLocaleString() : "—"}</span>
                        {historyType === "purchase" ? (
                          <span style={{ fontSize: 13 }}>{row.acquisition_label || "—"}</span>
                        ) : null}
                        {historyType === "redemption" ? (
                          <span style={{ fontSize: 13 }}>{secondaryTxnStatusLabel(row.status)}</span>
                        ) : null}
                        <span>{formatShare(row.shares)}</span>
                        <span>{bdtFull.format(Number(row.price_per_share || 0))}</span>
                      </div>
                      {historyType === "redemption" &&
                      String(row.status || "").toUpperCase() === "COMPLETED" &&
                      (row.net_cash_bdt != null || Number(row.fee_bdt || 0) > 0) ? (
                        <div className="muted" style={{ fontSize: 12, marginTop: 4, paddingLeft: 4 }}>
                          Net paid {bdtFull.format(Number(row.net_cash_bdt ?? 0))}
                          {Number(row.fee_bdt || 0) > 0 ? ` · fees ${bdtFull.format(Number(row.fee_bdt))}` : null}
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </div>
        </div>
      )}

    </div>
  );
}
